// ============================================================================
// eszkozMod.js — "saját eszköz" / "közös vagy kölcsönkapott eszköz" (2026-10-22, a titkár #63 javaslata).
//
// MIÉRT: a bejelentkezés és a Firestore helyi gyorsítótára alapból tartós (bezárás után is megmarad), ami a saját telefonon
// kényelmes, de közös számítógépen a következő használó előtt az előző tag beszélgetéseit hagyná. Az első használatkor ezért a tag
// választ, és a választás eszközönként megmarad:
//  - "sajat" (alapértelmezés, a régi viselkedés): tartós bejelentkezés + tartós (IndexedDB) gyorsítótár;
//  - "kozos": a bejelentkezés csak a lap / böngészőmunkamenet erejéig él (bezáráskor kilép), a Firestore csak memóriában tart adatot,
//    és a korábbi tartós gyorsítótár törlődik - a törzsszöveg nem kerül tartós tárba.
// A Firestore gyorsítótárát indítás után nem lehet átállítani, ezért a váltás a lap újratöltésével lép életbe (a belépő képernyőn
// ez észrevétlen). A modul tiszta döntéseket tartalmaz (Node-ban tesztelhető); a bekötés a firebaseClient.js-ben és a main.js-ben van.
// ============================================================================

export const ESZKOZ_MOD_KULCS = "l0_eszkozMod";
export const ESZKOZ_TORLENDO_KULCS = "l0_eszkozTorlendo";
export const ESZKOZ_TORLES_EREDMENY_KULCS = "l0_eszkozTorlesEredmeny";
export const ESZKOZ_TORLES_ESEMENY = "l0-eszkoz-torles";
export const MOD_SAJAT = "sajat";
export const MOD_KOZOS = "kozos";

function tarolo(win) {
  try {
    return win && win.localStorage ? win.localStorage : null;
  } catch (e) {
    return null;
  }
}

/** A mentett mód, vagy null, ha még nem választott (vagy nem olvasható a tároló). */
export function eszkozModOlvas(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  try {
    const v = t ? t.getItem(ESZKOZ_MOD_KULCS) : null;
    return v === MOD_SAJAT || v === MOD_KOZOS ? v : null;
  } catch (e) {
    return null;
  }
}

/** A tényleges mód: nincs választás = "sajat" (a régi, tartós viselkedés). */
export function hatalyosMod(win) {
  return eszkozModOlvas(win) || MOD_SAJAT;
}

/**
 * A mód mentése. Közösre váltáskor a korábbi tartós gyorsítótár törlendő (jelzés a következő indításra).
 * @returns {boolean} igaz, ha a hatályos mód ténylegesen változott (újratöltés kell)
 */
export function eszkozModIr(mod, win = typeof globalThis !== "undefined" ? globalThis : {}) {
  if (mod !== MOD_SAJAT && mod !== MOD_KOZOS) return false;
  const elozo = hatalyosMod(win);
  const t = tarolo(win);
  try {
    if (t) {
      t.setItem(ESZKOZ_MOD_KULCS, mod);
      if (mod === MOD_KOZOS) t.setItem(ESZKOZ_TORLENDO_KULCS, "1");
      t.removeItem(ESZKOZ_TORLES_EREDMENY_KULCS); // az új váltás eredménye még nem ismert (egy régi "ok" ne látsszon)
    }
  } catch (e) {
    /* a tároló nem írható: a mód nem tartható meg, a régi viselkedés marad */
  }
  return elozo !== hatalyosMod(win);
}

/** Közös eszköz: a bejelentkezés csak a böngészőmunkamenet erejéig él. */
export function authMegorzes(mod) {
  return mod === MOD_KOZOS ? "session" : "tartos";
}

/** Közös eszköz: a Firestore soha nem használ tartós tárat. */
export function tartosGyorsitotarEngedett(mod) {
  return mod !== MOD_KOZOS;
}

/** Kell-e törölni a korábbi tartós gyorsítótárat indításkor? (közös mód + jelzés) */
export function tartosTorlesKell(mod, win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  try {
    return mod === MOD_KOZOS && Boolean(t && t.getItem(ESZKOZ_TORLENDO_KULCS) === "1");
  } catch (e) {
    return false;
  }
}

export function tartosTorlesKesz(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  try {
    if (t) t.removeItem(ESZKOZ_TORLENDO_KULCS);
  } catch (e) {
    /* nem kritikus */
  }
}

/** A Firestore tartós gyorsítótárának IndexedDB-neve (a Firebase SDK 10.x elnevezése). */
export function firestoreAdatbazisNev(projektAzonosito) {
  return `firestore/[DEFAULT]/${projektAzonosito}/main`;
}

/**
 * A korábbi tartós tár törlésének EREDMÉNYE ("ok" | "blocked" | "hiba"). A váltást csak sikeres törlés után tekintjük késznek (a titkár #65):
 * ha egy másik megnyitott fül tartja nyitva az adatbázist, a törlés "blocked", és a program kiírja az okot. Az esemény újrarajzolást kér.
 */
export function torlesEredmenyIr(eredmeny, win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  try {
    if (t) {
      t.setItem(ESZKOZ_TORLES_EREDMENY_KULCS, eredmeny);
      if (eredmeny === "ok") t.removeItem(ESZKOZ_TORLENDO_KULCS);
    }
  } catch (e) {
    /* nem kritikus */
  }
  try {
    if (win && typeof win.dispatchEvent === "function" && typeof win.Event === "function") win.dispatchEvent(new win.Event(ESZKOZ_TORLES_ESEMENY));
  } catch (e) {
    /* nem kritikus */
  }
}

/** A közös eszközre váltás állapota a belépő képernyőnek: null (nem közös mód / nincs mit mutatni), "folyamatban", "blocked", "hiba", "ok". */
export function torlesAllapot(mod, win = typeof globalThis !== "undefined" ? globalThis : {}) {
  if (mod !== MOD_KOZOS) return null;
  const t = tarolo(win);
  try {
    if (!t) return null;
    const eredmeny = t.getItem(ESZKOZ_TORLES_EREDMENY_KULCS);
    if (t.getItem(ESZKOZ_TORLENDO_KULCS) === "1") return eredmeny === "blocked" || eredmeny === "hiba" ? eredmeny : "folyamatban";
    return eredmeny === "ok" ? "ok" : null;
  } catch (e) {
    return null;
  }
}

/** A belépést tiltani kell, amíg a régi tár törlése nem sikerült (különben a következő használó látná az előző tartalmát). */
export function belepesTiltva(allapot) {
  return allapot === "blocked" || allapot === "hiba";
}

// --- v143: az SDK TARTÓS gyorsítótára megszűnt (saját offline tár váltja, docs/OFFLINE_TERV_2026-10-22.md); a régi adatbázist MINDEN eszközön egyszer töröljük. ---
export const SDK_TAR_MIGRALVA_KULCS = "l0_sdkTarMigralva";

/** Igaz, ha a régi (tartós) SDK-adatbázist még nem töröltük ezen az eszközön. */
export function sdkTarMigralasKell(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  try {
    return !(t && t.getItem(SDK_TAR_MIGRALVA_KULCS) === "1");
  } catch (e) {
    return true;
  }
}

export function sdkTarMigralva(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  try {
    if (t) t.setItem(SDK_TAR_MIGRALVA_KULCS, "1");
  } catch (e) {
    /* nem kritikus: legközelebb újra próbálja */
  }
}
